<template>
  <div>
    <div>
      <el-row style="margin: 10px 0px 30px 0px;">
        <el-col :span="7">
          <div class="button-group-container">
            <el-popover placement="right" ref="newRecordPopover" :title="'New Inpatient Record'" width="300" trigger="click" @after-enter="handleNewRecordEnter">
              <el-form size="mini" id="addForm" :model="addForm" :inline="true" ref="addForm" :rules="rules" label-position="left" label-width="180px" v-loading="isPosting" element-loading-spinner="atom-audit-loader">
                <el-form-item prop="patientNumber" label="New Patient Number">
                  <input id="newPatientNumber" style="height: 22px;" class="hia-el-input el-input__inner" type="addReview" v-model="addForm.patientNumber" maxlength="12" />
                </el-form-item>

                <el-form-item v-if="!this.project.clientId" label="Facility" prop="clientId">
                  <hia-el-select v-model="addForm.clientId" placeholder="Facility" :filterable="true" itemkey="key" label="value" :dropdowns="dropdowns.clients.list"> </hia-el-select>
                </el-form-item>

                <center>
                  <el-button-group style="margin-top: 10px;">
                    <el-button class="hiaButton" plain round type="text" size="mini" @click="closeNewRecordPopover">Cancel</el-button>
                    <el-button class="hiaButton" round :disabled="!addForm.patientNumber" size="mini" @click="addReview">Add </el-button>
                  </el-button-group>
                </center>
              </el-form>

              <el-button class="hiaButton" slot="reference" :disabled="getSelectedRecords().length > 0 || (readOnly() && !projectManagement())" plain round size="mini" icon="el-icon-plus">New Record</el-button>
            </el-popover>

            <el-button :disabled="readOnly() && !projectManagement()" class="hiaButton" plain round v-if="getSelectedRecords().length > 0" size="mini" @click="showMassUpdateDialog = true">Mass Update </el-button>
            <el-button :disabled="readOnly() && !projectManagement()" class="hiaButton" plain round v-if="getSelectedRecords().length > 0" size="mini" @click="showMassDeleteDialog = true">Mass Delete </el-button>
            <el-popover v-if="getSelectedRecords().length > 0" placement="bottom" width="320" trigger="click">
              <p>Use the <strong>Rebuttal Tracking</strong> tab to Notify Coders.</p>
              <el-button slot="reference" :disabled="readOnly() && !projectManagement()" class="hiaButton" plain round size="mini">Notify Coders </el-button>
            </el-popover>
            <el-button :disabled="readOnly() && !projectManagement()" class="hiaButton" plain round v-if="tenantSetup.enableLevel2Audits && getSelectedRecords().length > 0" size="mini" @click="showLevel2AuditDialog = true">Level 2 Import </el-button>
          </div>
        </el-col>
        <el-col :span="10">
          <center class="stats">
            <el-badge class="item" :class="{ 'stats-loading': statsLoading }" :value="statistics.allRecords">
              <el-tag type="primary" size="small">All Records</el-tag>
            </el-badge>
            <el-badge class="item" :class="{ 'stats-loading': statsLoading }" :value="statistics.notStarted">
              <el-tag type="primary" size="small">Not Started</el-tag>
            </el-badge>
            <el-badge class="item" :class="{ 'stats-loading': statsLoading }" :value="statistics.inProgress">
              <el-tag type="primary" size="small">In Progress</el-tag>
            </el-badge>
            <el-badge class="item" :class="{ 'stats-loading': statsLoading }" :value="statistics.preliminary">
              <el-tag type="primary" size="small">Preliminary</el-tag>
            </el-badge>
            <el-badge class="item" :class="{ 'stats-loading': statsLoading }" :value="statistics.readyForCoder">
              <el-tag type="primary" size="small">Ready For Coder</el-tag>
            </el-badge>
            <el-badge class="item" :class="{ 'stats-loading': statsLoading }" :value="statistics.completed">
              <el-tag type="primary" size="small">Completed</el-tag>
            </el-badge>
          </center>
        </el-col>
        <el-col :span="7">
          <el-row :gutter="2">
            <el-col :span="24">
              <el-input id="globalSearch" type="text" size="mini" placeholder="filter all..." v-model="filterCriteria" v-on:keyup.native="runGlobalFilter($event)"> </el-input>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
      <div class="margin-top-5">
        <v-server-table v-if="toggleTable" class="hiaTable" id="ipReviewListGrid" ref="ipReviewListGrid" @loading="tableLoader = true" @loaded="tableLoader = false" :columns="columns" :options="options" v-loading="tableLoader" element-loading-spinner="atom-audit-loader">
          <template slot="selected" slot-scope="props">
            <input :disabled="readOnly() && !projectManagement()" type="checkbox" v-model="props.row.checked" @click="selectRecord(props.row, $event)" />
          </template>
          <template slot="patientNumber" slot-scope="props">
            <div>
              <router-link v-bind:to="'/reviews/inpatient/' + props.row.drgTableId">{{ props.row.patientNumber }} </router-link>
            </div>
          </template>
          <template slot="dischargeDate" slot-scope="props">
            <div>
              {{ props.row.dischargeDate ? formatDate(props.row.dischargeDate) : '' }}
            </div>
          </template>
          <template slot="admitDate" slot-scope="props">
            <div>
              {{ props.row.admitDate ? formatDate(props.row.admitDate) : '' }}
            </div>
          </template>
          <template slot="reviewDate" slot-scope="props">
            <div>
              {{ props.row.reviewDate ? formatDate(props.row.reviewDate) : '' }}
            </div>
          </template>

          <template slot="recordTags" slot-scope="props">
            <template v-if="props.row.recordTags">
              <div class="el-tag el-tag--mini el-tag--light" style="max-width: 100px; " v-for="tag in props.row.recordTags.split(',')" :key="tag">
                <div :title="tag" style="overflow: hidden; text-overflow: ellipsis;">
                  {{ tag }}
                </div>
              </div>
            </template>
          </template>

          <template slot="level2ReasonForChage" slot-scope="props">
            <div>
              {{ props.row.reviewLevel !== 'Coding Review' ? props.row.level2ReasonForChage : '' }}
            </div>
          </template>
          <template slot="hasAP" slot-scope="props">
            <div v-if="props.row.hasAP != null">
              <center>
                <i class="el-icon-check checkSize" :title="props.row.hasAP"></i>
              </center>
            </div>
          </template>
          <template slot="delete" slot-scope="props">
            <el-popover v-if="(readOnly() && projectManagement()) || !readOnly()" :popper-class="'delete_' + props.row.drgTableId" trigger="click" placement="left" width="190">
              <center>
                <p>Delete this Chart?</p>
                <el-button-group>
                  <el-button class="hiaButton" plain round type="text" size="mini" @click="deleteChart(props, true)">Cancel</el-button>
                  <el-button class="hiaButton" plain round size="mini" @click="deleteChart(props)">Confirm</el-button>
                </el-button-group>
              </center>
              <div slot="reference">
                <!-- <button :disabled="tableLoader" size="mini" type="button" title="Delete Chart" class="deleteButton" style="float: right;"></button> -->
                <div title="delete" style="text-align: center; cursor: pointer;">
                  <i class="el-icon-close delete-button"></i>
                </div>
              </div>
            </el-popover>
          </template>
          <template slot="reimbDiff" slot-scope="props">
            <span v-if="props.row.reimbDiff !== 0" :class="`${props.row.reimbDiff < 0 ? 'negative' : ''}`" :title="`Weight Diff: ${props.row.weightDiff}`"> ${{ formatMoney(props.row.reimbDiff) }}</span>
          </template>
        </v-server-table>
      </div>
    </div>
    <div id="dialogs">
      <el-dialog :title="ipListSelected.length > 0 ? 'Notify coders' : 'No Records Selected'" :visible.sync="showNotifyCoders">
        <v-client-table class="hiaTable" id="ipReviewListNotifyCoders" ref="ipReviewListNotifyCoders" :data="notifyCodersData" :columns="['coder', 'coderEmail', 'recordCount']" :options="notifyCodersOptions" element-loading-spinner="atom-audit-loader">
          <template slot="coder" slot-scope="props">
            {{ props.row.coder ? props.row.coder : '[not specified]' }}
          </template>
          <template slot="recordCount" slot-scope="props">
            {{ props.row.records.length }}
          </template>
          <template slot="child_row" slot-scope="props">
            <v-client-table style="width: 97%;float: right;" class="hiaTable" :data="props.row.records" :columns="['patientNumber', 'finalOutcome', 'agreeWithCodes']" :options="notifyCodersOptions" element-loading-spinner="atom-audit-loader">
              <template slot="patientNumber" slot-scope="props">
                <a href="" @click.stop.prevent="reviewListChildRowLink(props)">{{ props.row.patientNumber }}</a>
              </template>
            </v-client-table>
          </template>
          <template slot> </template>
        </v-client-table>

        <small>Notifications can only be sent to coders with email addresses</small>
        <span slot="footer">
          <el-button-group>
            <el-button @click="postNotifyCoders()" size="mini" plain round>Confirm</el-button>
            <el-button @click="showNotifyCoders = false" size="mini" plain round>Cancel</el-button>
          </el-button-group>
        </span>
      </el-dialog>

      <InpatientReviewsMassUpdate v-if="showMassUpdateDialog" @massupdate="handleMassUpdate" @close="showMassUpdateDialog = false" :localDropdowns="dropdowns" :showMassUpdateDialog="showMassUpdateDialog" :selectedRecords="ipListSelected" :selectedClients="getSelectedClients()"> </InpatientReviewsMassUpdate>
      <ReviewsMassDelete v-if="showMassDeleteDialog" @massDelete="runMassDelete" @close="closeMassDeleteDialog()" :idType="'drgTableId'" :showMassDeleteDialog="showMassDeleteDialog" :selectedRecords="ipListSelected" :massDeleteData="massDeleteData" :massDeleteLoading="massDeleteLoading"> </ReviewsMassDelete>

      <el-dialog
        :title="ipListSelected.length > 0 ? 'Level 2 Import' : 'No Records Selected'"
        :visible.sync="showLevel2AuditDialog"
        @close="
          copyToLevel2SuccessMessage = false
          copyToLevel2ErrorMessage = null
        "
      >
        <div v-loading="copyToLevel2Loading" element-loading-spinner="atom-audit-loader">
          <template v-if="!copyToLevel2SuccessMessage">
            <p style="margin: 0 0 10px 0; font-weight: 500; color: #303133; font-size: 14px;"><span style="color: #f56c6c; margin-left: 4px; margin-right: 4px;">*</span>Select Target Project</p>
            <projectSelector v-model="targetLevel2ProjectId" @selected="targetLevel2Project = $event" :project="targetLevel2Project" :defaultQuery="{ projectTypeId: project.projectTypeId }"> </projectSelector>

            <div style="display: flex; align-items: flex-start; margin-top: 20px; padding: 12px 16px; background-color: #f4f4f5; border-left: 4px solid #409eff; border-radius: 4px;">
              <i class="el-icon-info" style="color: #409eff; font-size: 16px; margin-right: 8px; margin-top: 2px; flex-shrink: 0;"></i>
              <small style="color: #606266; line-height: 1.5; display: block; flex: 1; word-break: normal; overflow-wrap: break-word;">Records will be copied to the target project and reset for Level 2 Auditing of the original auditor's work.</small>
            </div>

            <el-alert v-if="copyToLevel2ErrorMessage" style="margin-top: 20px;" type="error" :closable="false" show-icon>
              {{ copyToLevel2ErrorMessage }}
            </el-alert>
          </template>

          <div v-if="copyToLevel2SuccessMessage" style="margin-top: 20px; font-size: 14px;">
            Records copied succesfully. Go to the target project <a :href="`/projects/${targetLevel2ProjectId}`" target="_blank">here <i class="el-icon-top-right"></i></a>
          </div>
          <div style="margin-top: 10px; height: 60px;">
            <el-button-group style="float: right; margin-top: 40px;">
              <el-button
                class="hiaButton"
                plain
                round
                type="text"
                @click="
                  showLevel2AuditDialog = false
                  copyToLevel2SuccessMessage = false
                  copyToLevel2ErrorMessage = null
                "
                >Close</el-button
              >
              <el-button class="hiaButton" plain round :disabled="!targetLevel2ProjectId || copyToLevel2Loading || copyToLevel2SuccessMessage" @click="createLevel2Audit()">Confirm</el-button>
            </el-button-group>
          </div>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions, mapMutations } from 'vuex'
  import InpatientReviewsMassUpdate from './InpatientReviewMassUpdate'
  import ReviewsMassDelete from '@/components/controls/ReviewsMassDelete'
  import projectSelector from '@/components/controls/projectSelector'

  import dateHelper from '@/mixins/date-helper'
  import hiaTable from '@/mixins/hiaTable'
  import HiaAuthorization from '@/mixins/hiaAuthorization'
  import debounce from 'debounce'
  import money from '@/mixins/money'

  export default {
    name: 'inpatientList',
    mixins: [dateHelper, hiaTable, HiaAuthorization, money],
    data() {
      return {
        massDeleteData: {
          deleted: [],
          notDeleted: []
        },
        vueTables2Query: {},
        loading: true,
        statsLoading: false,
        toggleTable: true,
        showMassDeleteDialog: false,
        showValidationDialog: false,
        showLevel2AuditDialog: false,
        massDelete: false,
        massDeleteLoading: false,
        validationsLoading: false,
        showNotifyCoders: false,
        targetLevel2ProjectId: null,
        targetLevel2Project: null,
        copyToLevel2SuccessMessage: false,
        copyToLevel2ErrorMessage: null,
        isPosting: false,
        copyToLevel2Loading: false,
        isActivated: true,
        showRecordStats: {
          show: false,
          recordId: 0
        },
        showAddReviewForm: false,
        showMassUpdateDialog: false,
        showImportRebuttalsDialog: false,
        doubleClickPreventer: false,
        massupdateDropdowns: [],
        copyToLevel2: {
          visible: false,
          projectID: 0,
          rules: {
            projectID: [{ required: true, message: 'Please set Project' }]
          }
        },
        addForm: {
          patientNumber: '',
          clientId: null
        },
        rules: {
          patientNumber: [{ required: true, message: 'Please set Patient Number', trigger: 'blur' }],
          clientId: [{ required: true, message: 'Please set Facility', trigger: 'none' }]
        },
        filterCriteria: '',
        tableLoader: false,
        dropdownsNeeded: ['reviewLevels', 'clients', 'reviewtypes', 'inpatientAuditors', 'payors', 'reviewStatus'],
        columns: ['selected', 'patientNumber', 'patientType', 'consultantNumber', 'recordTags', 'mrNumber', 'payorName', 'coder', 'cdiStaff', 'physicianName', 'dischargeDate', 'grouper', 'reasonForChange', 'originalDrg', 'newDrg', 'patientAge', 'originalPoo', 'disposition', 'recommendedPatStatus', 'recommendedPoo', 'reviewDate', 'status', 'auditResult', 'agreeWithCodes', 'finalOutcome', 'responseCount', 'comments', 'reimbDiff', 'delete'],
        options: {
          actionsPosition: 'right',
          requestFunction: (data) => {
            return this.requestData(data)
          },
          debounce: 600,
          filterByColumn: true,
          listColumns: {
            reviewLevel: [],
            finalOutcome: []
          },
          orderBy: {
            column: 'patientNumber',
            ascending: true
          },
          columnsClasses: {
            selected: 'selected',
            comments: 'customComments',
            delete: 'delete'
          },
          headings: {
            selected: (h) => {
              return h('input', {
                attrs: {
                  type: 'checkbox',
                  id: 'selectAllCheckboxIpList',
                  disabled: this.readOnly() && !this.projectManagement()
                },
                on: {
                  click: (e) => {
                    this.localSelectAll(e.srcElement.checked)
                  }
                },
                ref: 'selectAllCheckbox'
              })
            },
            patientNumber: 'Patient Number',
            agreeWithCodes: 'Coder Agrees',
            finalOutcome: 'Final Outcome',
            patientType: 'Pat Type',
            status: 'Review Status',
            consultantNumber: 'Auditor',
            recordTags: 'Record Tags',
            payorName: 'Payor',
            physicianName: 'Provider',
            dischargeDate: 'Discharge Date',
            reasonForChange: 'Reason F Change',
            originalDrg: 'Orig. DRG',
            newDrg: 'New DRG',
            patientAge: 'Age',
            originalPoo: 'POO',
            disposition: 'Pat Status',
            recommendedPatStatus: 'Rec Pat Status',
            ipreviewType: 'Review Type',
            excludeRecord: 'Exclude Record',
            dedicatedStaffReview: 'Dedicated Staff Review',
            recommendedPoo: 'Rec POO',
            subClientNumber: 'Sub Client',
            originalReviewer: 'Orig Reviewer',
            originalReviewDate: 'Orig Review Date',
            originalClientNumber: 'Orig Client',
            level2ReasonForChage: 'Lvl2 Reason F Change',
            level2Drg: 'Lvl2 DRG',
            level2PatientStatus: 'Lvl2 Pat Status',
            level2Poo: 'Lvl2 POO',
            reviewLevel: 'Review Level',
            cdiStaff: 'CDI Staff',
            comments: 'Comments',
            mrNumber: 'MR Number',
            recordStatus: 'Record Status',
            reviewDate: 'Review Date',
            rebuttalCount: 'Rebuttal Count',
            rebuttalActiveCount: 'Active Rebuttals',
            rebuttalRespondedCount: 'Completed Rebuttals',
            hasAP: 'Has Action Plan',
            responseCount: 'Response Count',
            auditResult: 'Audit Result',
            reimbDiff: 'Reimb. Diff',
            originalAuditor: 'Original Auditor',
            delete: ''
          },
          perPage: 25,
          texts: {
            filter: '',
            filterBy: '...'
          },
          highlightMatches: true,
          sortIcon: { is: 'none' }
          // columnsDropdown: true
        },
        notifyCodersColumns: ['patientNumber', 'coder', 'coderEmail', 'finalOutcome', 'agreeWithCodes'],
        notifyCodersOptions: {
          filterByColumn: true,
          listColumns: {
            reviewLevel: []
          },
          orderBy: {
            column: 'patientNumber',
            ascending: true
          },
          columnsClasses: {
            recordTags: 'tagsCol'
          },
          headings: {
            patientNumber: 'Patient Number',
            agreeWithCodes: 'Coder Agrees',
            finalOutcome: 'Final Outcome',
            coderEmail: 'Email',
            coder: 'Coder',
            recordCount: 'Record Count'
          },
          perPage: 25,
          texts: {
            filter: '',
            filterBy: '...'
          },
          highlightMatches: true,
          sortIcon: { is: 'none' },
          uniqueKey: 'coderId'
          // columnsDropdown: true
        }
      }
    },
    methods: {
      ...mapActions('inpatientReviews/', ['POST_IP_RECORD', 'LOAD_IP_LIST_PAGINATED', 'GET_IPLIST_STATS', 'IP_RECORD_EXISTS', 'DELETE_IP_RECORD', 'GET_IP_LIST_ALLIDS', 'POST_NOTIFY_CODERS', 'RUN_IP_MASSDELETE', 'RUN_IP_COPYTOLEVEL2']),
      ...mapActions('managedLists/tenantSetup/', ['GET_TENANT_SETUP']),
      ...mapActions('dropdowns/', ['GET_DROPDOWNS', 'CONVERT_DROPDOWN_COLUMNFILTERARRAY']),
      ...mapMutations('inpatientReviews/', ['SET_IPLIST_SELECTED', 'CLEAR_IPLIST_SELECTED', 'SET_IPLIST_REFRESH_FUNC']),
      async requestData(data) {
        try {
          data.projectID = this.$route.params.id
          data.globalSearch = this.filterCriteria
          this.vueTables2Query = data
          this.statsLoading = true
          const resultArray = await Promise.all([this.GET_IPLIST_STATS(data), this.LOAD_IP_LIST_PAGINATED(data)])
          this.options.listColumns.finalOutcome = resultArray[1].finalOutcomeFilter
          this.statsLoading = false
          return resultArray[1]
        } catch (err) {
          console.log(err)
        }
      },
      async createLevel2Audit() {
        try {
          this.copyToLevel2Loading = true
          this.copyToLevel2ErrorMessage = null
          const payload = {
            records: this.getSelectedRecords(),
            targetProjectId: this.targetLevel2ProjectId
          }
          await this.RUN_IP_COPYTOLEVEL2(payload)
          this.copyToLevel2SuccessMessage = true
          this.copyToLevel2Loading = false
        } catch (err) {
          this.copyToLevel2Loading = false
          this.copyToLevel2ErrorMessage = err.message || 'An error occurred while copying records to Level 2 Audit'
          console.log(err)
        }
      },
      async localSelectAll(checked) {
        const table = this.$refs.ipReviewListGrid.$el
        const checkboxes = table.querySelectorAll('input[type ="checkbox"]')

        if (checked) {
          this.tableLoader = true
          await this.GET_IP_LIST_ALLIDS(this.vueTables2Query)
          for (let i = 0; i < checkboxes.length; i++) {
            checkboxes[i].checked = true
          }
          this.tableLoader = false
        } else {
          this.CLEAR_IPLIST_SELECTED()
          for (let i = 0; i < checkboxes.length; i++) {
            checkboxes[i].checked = false
          }
        }
      },
      getValidation() {
        const payload = this.getSelectedRecords()
        if (payload.length > 0) {
          this.validationsLoading = true
          this.POST_VALIDATION(payload).then(() => {
            this.validationsLoading = false
          })
        }
      },
      getValidationDialogTitle() {
        if (this.getSelectedRecords().length === 0) {
          return 'No Records Selected'
        }
        if (this.validationsLoading) {
          return 'Validation Results - loading...'
        }
        return `Validation Results - ${this.ipListRecordValidations.length} ${this.ipListRecordValidations.length === 1 ? 'Record' : 'Records'} With Errors`
      },
      selectRecord(row, e) {
        const payload = {
          checked: e.srcElement.checked,
          clientName: row.clientName,
          drgTableId: row.drgTableId,
          patientNumber: row.patientNumber,
          coder: row.coder,
          coderId: row.coderId,
          consultantNumber: row.consultantNumber,
          projectId: row.projectId,
          projectName: row.projectName,
          coderEmail: row.coderEmail,
          finalOutcome: row.finalOutcome,
          agreeWithCodes: row.agreeWithCodes
        }

        this.SET_IPLIST_SELECTED(payload)
      },
      async handleMassUpdate(massUpdateModel) {
        this.showMassUpdateDialog = false
        this.localSelectAll(false) // clear all the selected checkboxes
        this.$refs.ipReviewListGrid.getData()
        if (this.refreshIpRebuttalTracking) {
          this.refreshIpRebuttalTracking()
        }
      },
      toggleAddReviewForm() {
        if (this.showAddReviewForm) {
          this.$refs.addForm.clearValidate()
          this.showAddReviewForm = false
          this.addForm.patientNumber = ''
          this.addForm.clientId = null
        } else {
          this.showAddReviewForm = true
        }
      },
      addReview() {
        // use isPosting to prevent double clicks adding two records
        if (!this.isPosting) {
          this.$refs.addForm.validate((valid) => {
            if (valid) {
              this.isPosting = true
              this.postInpatientReview(this.addForm.patientNumber)
            } else {
              console.log('error submit!!')
              return false
            }
          })
        }
      },
      async postInpatientReview(patientNumber) {
        try {
          const newReview = {
            patientNumber: patientNumber.trim(),
            clientId: this.project.clientId ? this.project.clientId : this.addForm.clientId,
            providerId: this.project.defaultProviderId,
            coderId: this.project.defaultCoderId,
            reviewDate: this.project.startDate,
            projectID: this.projectPK
          }

          const response = await this.IP_RECORD_EXISTS(newReview)

          if (response.data.message) {
            this.$confirm(response.data.message, 'Create record', {
              confirmButtonText: 'Continue',
              cancelButtonText: 'Cancel',
              type: 'warning'
            }).then(
              () => {
                this.doPost(newReview)
              },
              (error) => {
                console.log(error)
                this.isPosting = false
              }
            )
          } else {
            this.doPost(newReview)
          }
        } catch (err) {
          console.log(err)
          this.isPosting = false
        }
      },
      async doPost(payload) {
        try {
          const response = await this.POST_IP_RECORD(payload)
          this.isPosting = false
          this.$refs.addForm.clearValidate()
          this.showAddReviewForm = false
          this.addForm.patientNumber = ''
          this.addForm.clientId = null
          this.$refs.ipReviewListGrid.getData()
          this.$router.push({ path: `/reviews/inpatient/${response.drgTableId}` })
        } catch (err) {
          this.isPosting = false
        }
      },
      runGlobalFilter: debounce(function() {
        this.$refs.ipReviewListGrid.setPage(1)
      }, 400),
      recordStatsActivate(condition, recordId) {
        this.showRecordStats = {
          show: condition,
          recordId: recordId
        }
      },
      getSelectedRecords() {
        const simpleArray = this.ipListSelected.map((y) => y.drgTableId)
        return [...new Set(simpleArray)]
      },
      getSelectedClients() {
        const simpleArray = this.ipListSelected.map((y) => y.clientNumber)
        return [...new Set(simpleArray)]
      },
      async deleteChart(props, closeOnly) {
        try {
          const popOver = document.querySelector('.delete_' + props.row.drgTableId)
          if (closeOnly) {
            popOver.style.display = 'none'
            return false
          }
          this.tableLoader = true
          popOver.style.display = 'none'

          await this.DELETE_IP_RECORD(props.row.drgTableId)
          this.$message({
            message: 'Chart Deleted Successfully',
            type: 'info'
          })
          this.$refs.ipReviewListGrid.getData()
        } catch (err) {
          this.tableLoader = false
          this.$message({
            message: err.message,
            type: 'error'
          })
        }
      },
      emitAlert(message) {
        // EventBus.$emit('alert', message)
      },
      runMassDelete() {
        this.massDeleteLoading = true
        this.RUN_IP_MASSDELETE(this.getSelectedRecords()).then(
          (response) => {
            this.massDeleteData = response.data
            const el = document.getElementById('selectAllCheckboxIpList')
            if (el) {
              el.checked = false
            }
            this.CLEAR_IPLIST_SELECTED()
            this.$refs.ipReviewListGrid.getData()
            this.massDeleteLoading = false
          },
          (error) => {
            this.closeMassDeleteDialog()
            this.emitAlert({
              message: 'Error Mass Deleting Records',
              type: 'error'
            })
            console.log(error)
            this.massDeleteLoading = false
          }
        )
      },
      closeMassDeleteDialog() {
        this.showMassDeleteDialog = false
        this.massDeleteData = {
          deleted: [],
          notDeleted: []
        }
      },
      initNotifyCoders() {
        this.showNotifyCoders = !this.showNotifyCoders
      },
      async postNotifyCoders() {
        const notifyCodersData = []
        for (let i = 0; i < this.notifyCodersData.length; i++) {
          const c = this.notifyCodersData[i]
          if (c.coderEmail) {
            notifyCodersData.push({ coder: c.coder, auditor: c.auditor, coderEmail: c.coderEmail, recordCount: c.records.length, myRecordsUrl: `/my-projects/${this.projectPK}?type=Inpatient` })
          }
        }

        try {
          this.showNotifyCoders = true
          await this.POST_NOTIFY_CODERS(notifyCodersData)
          this.showNotifyCoders = false
          this.$message({
            message: 'Coders Notification Successful',
            type: 'success'
          })
        } catch (err) {
          this.showNotifyCoders = false
          this.$message({
            message: err.message,
            type: 'error'
          })
        }
      },
      reviewListChildRowLink(props) {
        this.showNotifyCoders = !this.showNotifyCoders
        this.$router.push(`/reviews/inpatient/${props.row.drgTableId}`)
      },
      handleNewRecordEnter() {
        const el = document.getElementById('newPatientNumber')
        el.focus()
      },
      closeNewRecordPopover() {
        const popper = this.$refs.newRecordPopover
        if (popper) {
          popper.doDestroy()
          popper.doClose()
        }
      },
      removeOriginalAuditorColumn() {
        const newCol = 'originalAuditor'
        const existingIdx = this.columns.indexOf(newCol)
        if (existingIdx !== -1) {
          this.columns.splice(existingIdx, 1)
          this.toggleTable = false
          this.$nextTick(() => {
            this.toggleTable = true
          })
        }
      },
      addOriginalAuditorColumn() {
        const newCol = 'originalAuditor'
        const recordTagsIdx = this.columns.indexOf('recordTags')
        const existingIdx = this.columns.indexOf(newCol)
        if (existingIdx === -1) {
          const insertIdx = recordTagsIdx !== -1 ? recordTagsIdx : this.columns.length
          this.columns.splice(insertIdx, 0, newCol)
          this.toggleTable = false
          this.$nextTick(() => {
            this.toggleTable = true
          })
        }
      }
    },
    watch: {
      'project.projectReviewTypeId': {
        handler() {
          if (this.project.projectReviewTypeId === 2) {
            this.addOriginalAuditorColumn()
          } else {
            this.removeOriginalAuditorColumn()
          }
        },
        deep: true
      }
    },
    computed: {
      ...mapState('inpatientReviews/', ['statistics', 'ipListSelected', 'ipListRecordValidations', 'refreshIpList']),
      ...mapState('rebuttalTracking/', ['refreshIpRebuttalTracking']),
      ...mapState('managedLists/projects/', ['projectPK', 'project']),
      ...mapState('dropdowns/', ['dropdowns']),
      ...mapState('user/', ['user']),
      ...mapState('managedLists/tenantSetup/', ['tenantSetup']),
      notifyCodersData() {
        if (this.ipListSelected.length) {
          const groupedData = this.ipListSelected.reduce((r, a) => {
            const found = r.find((f) => {
              return f.coderId === a.coderId
            })
            if (found) {
              found.records.push(a)
            } else {
              r.push({ coderId: a.coderId, coder: a.coder, auditor: this.user.fullName, coderEmail: a.coderEmail, records: [a] })
            }
            return r
          }, [])
          return [...new Set(groupedData)]
        }
        return []
      }
      // ...mapState('profile/', ['userProfile'])
    },
    created: function() {
      this.loading = true
      if (this.project.projectReviewTypeId === 2) {
        this.addOriginalAuditorColumn()
      }
      Promise.all([this.GET_DROPDOWNS(this.dropdownsNeeded), this.GET_TENANT_SETUP()]).then(() => {
        this.CONVERT_DROPDOWN_COLUMNFILTERARRAY('reviewLevels').then((response) => {
          this.options.listColumns.reviewLevel = response
          this.loading = false
        })
      })

      const refreshFunc = () => {
        if (this.$refs.ipReviewListGrid) {
          this.$refs.ipReviewListGrid.getData()
        }
      }

      this.SET_IPLIST_REFRESH_FUNC(refreshFunc)
    },
    activated: function() {
      this.refreshIpList()
      this.isActivated = true
    },
    deactivated: function() {
      this.isActivated = false
      // clear any old validation messages, validation messages can be left around because the form validation gets triggered natively by
      // the browser when users hit the enter key in the form
      if (this.$refs.addForm) {
        this.$refs.addForm.clearValidate()
      }
      this.localSelectAll(false)
      this.CLEAR_IPLIST_SELECTED()
    },
    components: {
      InpatientReviewsMassUpdate,
      ReviewsMassDelete,
      projectSelector
    }
  }
</script>

<style scoped>
  >>> .tagsCol {
    max-width: 100px;
    overflow: hidden;
  }
</style>

<style>
  .my-multiselect .multiselect {
    background-color: blue;
  }

  #globalSearch {
    width: 300px;
    padding: 0px 0px 0px 10px;
    float: right;
  }

  .hideColumn {
    display: none;
  }

  .item {
    margin-top: 3px;
    margin-right: 35px;
  }

  .el-badge__content {
    background-color: #20a0ff;
  }

  .stats-loading {
    opacity: 0.5;
  }

  input[name='vf__selected'],
  input[name='vf__Stats'] {
    display: none;
  }

  .checkSize {
    font-size: 17px;
  }

  .customComments {
    overflow: auto;
    word-break: break-all;
  }

  .sortableColumns {
    width: 80%;
  }

  .dragOn {
    cursor: move;
  }

  th.delete div {
    display: none;
  }

  .VueTables__child-row-toggler--closed::before {
    content: '+';
  }

  .VueTables__child-row-toggler--open::before {
    content: '-';
  }

  .el-button + .el-button {
    margin-left: 0px;
  }

  .button-group-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: flex-start;
  }
</style>
